import './candidate-match-scores.scss';

import CloseIcon from '@mui/icons-material/Close';
import DomainIcon from '@mui/icons-material/Domain';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import BackArrowIcon from 'assets/images/iPrise/BackArrow.svg';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import StylishLoader from '../components/StylishLoader';

interface CandidateMatchScoresProps {
  candidates?: any[];
  loading?: boolean;
  goal?: any;
  selectedCandidateId?: string;
  jobTitle?: string;
  expFrom?: number;
  expTo?: number;
  isModal?: boolean;
  onClose?: () => void;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  try {
    const activeDate = new Date(dateStr);
    if (isNaN(activeDate.getTime())) return '';

    const currentDate = new Date();
    const diffTime = currentDate.getTime() - activeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return `${diffDays} days ago`;
    }
  } catch (e) {
    return '';
  }
};

const CandidateMatchScoresScreen: React.FC<CandidateMatchScoresProps> = ({
  candidates: propsCandidates,
  loading: propsLoading,
  goal: propsGoal,
  selectedCandidateId: propsSelectedCandidateId,
  jobTitle: propsJobTitle,
  expFrom: propsExpFrom,
  expTo: propsExpTo,
  isModal = false,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state;
  const candidates =
    propsCandidates !== undefined
      ? propsCandidates
      : state?.Params?.candidates || [];
  const loading =
    propsLoading !== undefined ? propsLoading : state?.Params?.loading || false;
  const goal = propsGoal !== undefined ? propsGoal : state?.Params?.goal;
  const selectedCandidateId =
    propsSelectedCandidateId !== undefined
      ? propsSelectedCandidateId
      : state?.Params?.selectedCandidateId;
  const jobTitle =
    propsJobTitle !== undefined ? propsJobTitle : state?.Params?.jobTitle;
  const expFrom =
    propsExpFrom !== undefined ? propsExpFrom : state?.Params?.expFrom;
  const expTo = propsExpTo !== undefined ? propsExpTo : state?.Params?.expTo;

  const [leftWidth, setLeftWidth] = useState(40); // Percentage
  const [isDragging, setIsDragging] = useState(false);
  const [formulaStepsExpanded, setFormulaStepsExpanded] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const screenWidth = window.innerWidth;
      const newWidthPercentage = (e.clientX / screenWidth) * 100;
      // Constrain between 20% and 80%
      const constrainedWidth = Math.max(20, Math.min(80, newWidthPercentage));
      setLeftWidth(constrainedWidth);
    },
    [isDragging],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const displayCandidates = selectedCandidateId
    ? candidates.filter((cand: any) => {
        return (
          String(cand.id) === String(selectedCandidateId) ||
          String(cand.candidate?.id) === String(selectedCandidateId)
        );
      })
    : candidates;

  const pageTitle = selectedCandidateId
    ? 'Candidate Match Score'
    : 'All Matched Candidates Scores';

  return (
    <Box className="candidate-match-scores-screen inter-font-family">
      {/* Header Section */}
      <Box className="screen-header">
        <Box className="header-left">
          {!isModal && (
            <IconButton className="back-button" onClick={() => navigate(-1)}>
              <img src={BackArrowIcon} alt="Back" />
            </IconButton>
          )}
          <Typography className="screen-title">{pageTitle}</Typography>
        </Box>
        {isModal && (
          <IconButton
            className="close-button"
            onClick={onClose}
            sx={{
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '8px',
            }}>
            <CloseIcon sx={{ color: '#111827' }} />
          </IconButton>
        )}
      </Box>

      {/* Main Content Area */}
      <Box className="content-area">
        {/* Left Panel: Goal & JD */}
        <Box className="left-panel" style={{ width: `${leftWidth}%` }}>
          {goal &&
            (() => {
              try {
                const goalData =
                  typeof goal === 'string' ? JSON.parse(goal) : goal;
                const introduction = goalData?.introduction;
                const requiredIdentity = goalData?.requiredIdentity;
                const mustHaveSkills = goalData?.skills?.mustHave || [];
                const primarySkills = goalData?.skills?.primary || [];
                const secondarySkills = goalData?.skills?.secondary || [];

                const minExp =
                  goalData?.yearsExpMin !== undefined &&
                  goalData?.yearsExpMin !== null
                    ? goalData.yearsExpMin
                    : expFrom;
                const maxExp =
                  goalData?.yearsExpMax !== undefined &&
                  goalData?.yearsExpMax !== null
                    ? goalData.yearsExpMax
                    : expTo;

                const experienceText =
                  minExp !== null && minExp !== undefined
                    ? `${String(minExp)}${
                        maxExp && maxExp !== minExp && maxExp !== 0
                          ? `-${String(maxExp)}`
                          : '+'
                      } years`
                    : null;

                return (
                  <Box className="goal-container">
                    {jobTitle && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            color: '#111827',
                            fontSize: '18px',
                            fontWeight: 700,
                            mb: 0.5,
                          }}>
                          {jobTitle}
                        </Typography>
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                          }}>
                          {requiredIdentity && (
                            <Box
                              sx={{
                                backgroundColor: '#ede9fe',
                                borderRadius: '4px',
                                color: '#6b21a8',
                                fontSize: '11px',
                                fontWeight: 600,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {requiredIdentity}
                            </Box>
                          )}
                          {experienceText && (
                            <Box
                              sx={{
                                backgroundColor: '#f3f4f6',
                                borderRadius: '4px',
                                color: '#374151',
                                fontSize: '11px',
                                fontWeight: 600,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {experienceText}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {introduction && (
                      <Typography
                        sx={{
                          color: '#374151',
                          fontSize: '13px',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          mb: 1,
                        }}>
                        {introduction}
                      </Typography>
                    )}

                    {(mustHaveSkills.length > 0 ||
                      primarySkills.length > 0) && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            alignItems: 'center',
                            color: '#4f46e5',
                            display: 'flex',
                            fontSize: '11px',
                            fontWeight: 600,
                            gap: 0.5,
                            mb: 1,
                          }}>
                          ● Primary Skills (
                          {mustHaveSkills.length + primarySkills.length})
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                          }}>
                          {mustHaveSkills.map((skill: string, i: number) => (
                            <Box
                              key={`must-${i}`}
                              sx={{
                                backgroundColor: '#e0e7ff',
                                borderRadius: '4px',
                                color: '#4338ca',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {skill}
                            </Box>
                          ))}
                          {primarySkills.map((skill: string, i: number) => (
                            <Box
                              key={`primary-${i}`}
                              sx={{
                                backgroundColor: '#e0e7ff',
                                borderRadius: '4px',
                                color: '#4338ca',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {skill}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {secondarySkills.length > 0 && (
                      <Box>
                        <Typography
                          sx={{
                            alignItems: 'center',
                            color: '#6b7280',
                            display: 'flex',
                            fontSize: '11px',
                            fontWeight: 600,
                            gap: 0.5,
                            mb: 1,
                          }}>
                          ○ Secondary Skills ({secondarySkills.length})
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.75,
                          }}>
                          {secondarySkills.map((skill: string, i: number) => (
                            <Box
                              key={i}
                              sx={{
                                backgroundColor: '#f3f4f6',
                                borderRadius: '4px',
                                color: '#4b5563',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {skill}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              } catch (e) {
                return (
                  <Box className="goal-container error-goal">
                    <Typography
                      sx={{
                        color: '#166534',
                        fontSize: '11px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        mb: 1,
                        textTransform: 'uppercase',
                      }}>
                      Goal
                    </Typography>
                    <Typography
                      sx={{
                        color: '#374151',
                        fontSize: '13px',
                        fontWeight: 400,
                        lineHeight: 1.6,
                      }}>
                      {typeof goal === 'string' ? goal : JSON.stringify(goal)}
                    </Typography>
                  </Box>
                );
              }
            })()}
        </Box>

        {/* Resizer Handle */}
        <Box
          className={`resizer ${isDragging ? 'dragging' : ''}`}
          onMouseDown={handleMouseDown}>
          <img
            src={require('assets/images/split-v.svg').default}
            alt="Resize"
            className="resizer-icon"
          />
        </Box>

        {/* Right Panel: Candidates List */}
        <Box
          className="right-panel"
          style={{ width: `calc(${100 - leftWidth}% - 12px)` }}>
          {loading ? (
            <StylishLoader
              text="Loading candidate scores..."
              fullPage={false}
              mode="inline"
            />
          ) : (
            displayCandidates?.map((cand: any, index: number) => {
              const candidate = cand;
              const matchPercentage = candidate?.matchPercentage || 0;
              const percentageDetail = candidate?.mathematicalBreakdown;

              let matchedSkills: any[] = [];
              let secondarySkills: any[] = [];
              let primarySkillAnalysis: any = null;
              let secondarySkillAnalysis: any = null;
              (percentageDetail ?? []).forEach((row: any) => {
                if (row?.component === 'Primary Skills') {
                  matchedSkills = matchedSkills.concat(
                    row?.matchedSkills ?? [],
                  );
                  primarySkillAnalysis = row?.skillAnalysis;
                }
                if (row?.component === 'Secondary Skills') {
                  secondarySkills = secondarySkills.concat(
                    row?.matchedSkills ?? [],
                  );
                  secondarySkillAnalysis = row?.skillAnalysis;
                }
              });
              const name =
                candidate?.candidateName ||
                candidate?.name ||
                (
                  (candidate?.candidate?.firstName ||
                    candidate?.firstName ||
                    '') +
                  ' ' +
                  (candidate?.candidate?.middleName?.trim() ||
                    candidate?.middleName?.trim() ||
                    '') +
                  ' ' +
                  (candidate?.candidate?.lastName?.trim() ||
                    candidate?.lastName?.trim() ||
                    '')
                ).trim() ||
                '-';

              return (
                <Box key={index} className="candidate-card">
                  {/* Header: Name and Match Score */}
                  <Box className="candidate-header">
                    <Box className="candidate-info">
                      <Typography className="candidate-name">{name}</Typography>
                      {candidate?.rank && (
                        <Box className="rank-badge">#{candidate?.rank}</Box>
                      )}
                      <Box className="candidate-meta">
                        {(candidate?.candidate?.city ||
                          candidate?.city ||
                          candidate?.candidate?.state ||
                          candidate?.state ||
                          candidate?.location) && (
                          <Box className="meta-item">
                            <LocationOnIcon className="meta-icon location-icon" />
                            <Typography className="meta-text">
                              {candidate?.candidate?.city ||
                                candidate?.city ||
                                candidate?.location ||
                                ''}
                              {(candidate?.candidate?.city ||
                                candidate?.city) &&
                              (candidate?.candidate?.state || candidate?.state)
                                ? ', '
                                : ''}
                              {candidate?.candidate?.state || candidate?.state}
                            </Typography>
                          </Box>
                        )}
                        {(candidate?.candidate?.overallExperience ||
                          candidate?.overallExperience ||
                          candidate?.yearsOfExperience) && (
                          <Box className="meta-item">
                            <WorkIcon className="meta-icon work-icon" />
                            <Typography className="meta-text">
                              {candidate?.candidate?.overallExperience ||
                                candidate?.overallExperience ||
                                candidate?.yearsOfExperience}{' '}
                              Years
                            </Typography>
                          </Box>
                        )}
                        {(candidate?.candidate?.workStatus?.name ||
                          candidate?.visaStatus ||
                          candidate?.workPermitDocuments?.[0]) && (
                          <Box className="meta-item">
                            <RocketLaunchIcon className="meta-icon visa-icon" />
                            <Typography className="meta-text">
                              {candidate?.candidate?.workStatus?.name ||
                                candidate?.visaStatus ||
                                candidate?.workPermitDocuments?.[0]}
                            </Typography>
                          </Box>
                        )}
                        {candidate?.candidate?.identity && (
                          <Box className="meta-item">
                            <DomainIcon className="meta-icon" />
                            <Typography className="meta-text">
                              {candidate?.candidate?.identity}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box className="score-container">
                      <Typography className="score-label">
                        Match Score
                      </Typography>
                      <Typography className="score-value">
                        {matchPercentage}%
                      </Typography>
                    </Box>
                  </Box>

                  {/* Score Breakdown Grid */}
                  {Array.isArray(percentageDetail) &&
                    percentageDetail?.length > 0 && (
                      <Box className="score-breakdown-grid">
                        {percentageDetail.map((row: any, i: number) => {
                          if (
                            row?.component === 'Identity Match' ||
                            row?.component === 'Secondary Skills'
                          )
                            return null;

                          const score = parseFloat(row?.score) || 0;
                          const max = parseFloat(row?.maxPercentage) || 0;
                          const percentage = Math.min(
                            max > 0 ? (score / max) * 100 : 0,
                            100,
                          );
                          const colors = [
                            '#a855f7',
                            '#06b6d4',
                            '#f59e0b',
                            '#10b981',
                            '#6366f1',
                          ];
                          const color = colors[i % colors.length];

                          const formattedScore =
                            score % 1 === 0
                              ? score.toString()
                              : score.toFixed(1);
                          const formattedMax = Math.round(max).toString();

                          const isLastActive =
                            row?.component === 'Candidate Last Active';
                          const lastActiveDate = isLastActive
                            ? candidate?.candidate?.dateLastActive ||
                              candidate?.dateLastActive ||
                              candidate?.candidate?.ceipalModifiedAt ||
                              candidate?.ceipalModifiedAt
                            : null;
                          const formattedDate = formatDate(lastActiveDate);

                          return (
                            <Box key={i} className="breakdown-item">
                              <Typography className="breakdown-label">
                                {row?.component}
                              </Typography>
                              <Box className="progress-bar-container">
                                <Box
                                  className="progress-bar-fill"
                                  style={{
                                    backgroundColor: color,
                                    width: `${percentage}%`,
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}>
                                {isLastActive && formattedDate ? (
                                  <Typography
                                    sx={{
                                      color: '#f97316', // Sleek orange color matching the last active bar
                                      fontFamily: 'Inter',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                    }}>
                                    Active: {formattedDate}
                                  </Typography>
                                ) : (
                                  <div />
                                )}
                                <Typography className="breakdown-value">
                                  {formattedScore}/{formattedMax}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                  {/* Role/Title Match Analysis */}
                  {(() => {
                    const roleTitleMatch = (percentageDetail ?? []).find(
                      (row: any) => row?.component === 'Role/Title Match',
                    );
                    if (!roleTitleMatch) return null;

                    const roleHistory = roleTitleMatch?.roleHistory || [];
                    const analysis = roleTitleMatch?.roleTitleAnalysis || {};
                    const formulas = analysis?.formulas || {};

                    return (
                      <Box className="role-title-analysis">
                        <Typography className="analysis-title">
                          Role/Title score calculation
                        </Typography>

                        <Box className="analysis-box">
                          <Box sx={{ mb: 1.5 }}>
                            <Typography className="analysis-score-text">
                              Score:{' '}
                              <Typography
                                component="span"
                                sx={{
                                  color: (() => {
                                    const score =
                                      analysis.calculatedScore ||
                                      roleTitleMatch?.score ||
                                      0;
                                    const maxScore =
                                      analysis.maxScore ||
                                      roleTitleMatch?.maxPercentage ||
                                      35;
                                    const percentage = (score / maxScore) * 100;

                                    if (percentage >= 85) return '#16a34a';
                                    if (percentage >= 70) return '#eab308';
                                    if (percentage >= 50) return '#f97316';
                                    return '#dc2626';
                                  })(),
                                  fontSize: '0.95rem',
                                  fontWeight: 700,
                                }}>
                                {analysis.calculatedScore ||
                                  roleTitleMatch?.score}
                              </Typography>
                              <Typography
                                component="span"
                                className="analysis-max-score">
                                {' '}
                                /{' '}
                                {analysis.maxScore ||
                                  roleTitleMatch?.maxPercentage}{' '}
                                points
                              </Typography>
                            </Typography>
                          </Box>

                          {analysis.calculationSteps &&
                            analysis.calculationSteps.length > 0 && (
                              <Box className="calculation-steps">
                                <Typography className="calculation-title">
                                  Calculation:
                                </Typography>
                                {analysis.calculationSteps.map(
                                  (step: any, stepIndex: number) => (
                                    <Box
                                      key={stepIndex}
                                      className="calculation-step">
                                      <Box className="step-badge">
                                        Step {step.step}
                                      </Box>
                                      <Typography className="step-value">
                                        {step.value}
                                      </Typography>
                                    </Box>
                                  ),
                                )}
                              </Box>
                            )}

                          <Box className="aggregation-info">
                            <Typography className="aggregation-text">
                              <span style={{ fontWeight: 600 }}>
                                Aggregation:
                              </span>{' '}
                              {analysis.aggregationMethod};{' '}
                              <span style={{ fontWeight: 600 }}>
                                Trajectory:
                              </span>{' '}
                              {analysis.trajectory};{' '}
                              <span style={{ fontWeight: 600 }}>T_adj =</span>{' '}
                              {analysis.trajectory === 'ascending'
                                ? '+0.05'
                                : analysis.trajectory === 'descending'
                                ? '-0.05'
                                : '0'}
                              ; {roleHistory.length}(roles) evaluated;{' '}
                              <span style={{ fontWeight: 600 }}>S_agg:</span>{' '}
                              {analysis.s_agg};{' '}
                              <span style={{ fontWeight: 600 }}>
                                W_current:
                              </span>{' '}
                              {analysis.w_current};{' '}
                              <span style={{ fontWeight: 600 }}>W_best:</span>{' '}
                              {analysis.w_best_historical}
                            </Typography>
                          </Box>

                          {Object.keys(formulas).length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Box
                                onClick={() =>
                                  setFormulaStepsExpanded(!formulaStepsExpanded)
                                }
                                className="formula-toggle">
                                <Typography className="formula-toggle-text">
                                  <Typography
                                    component="span"
                                    className={`chevron ${
                                      formulaStepsExpanded ? 'expanded' : ''
                                    }`}>
                                    ›
                                  </Typography>
                                  Formula steps
                                </Typography>
                              </Box>

                              {formulaStepsExpanded && (
                                <Box className="formula-steps-container">
                                  {Object.entries(formulas).map(
                                    ([key, value]: [string, any]) => (
                                      <Box key={key}>
                                        <Typography className="formula-step-text">
                                          {String(value)}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                        {/* Role History Table */}
                        {roleHistory.length > 0 && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography
                              sx={{
                                color: '#374151',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                mb: 1,
                              }}>
                              Role History (weighted)
                            </Typography>
                            <Box
                              sx={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden',
                              }}>
                              <Box
                                component="table"
                                sx={{
                                  borderCollapse: 'collapse',
                                  fontSize: '0.8rem',
                                  width: '100%',
                                }}>
                                <Box component="thead">
                                  <Box component="tr">
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                      }}>
                                      Title
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                        width: '60px',
                                      }}>
                                      Rank
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                        width: '70px',
                                      }}>
                                      S_base
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                        width: '80px',
                                      }}>
                                      W_role
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                      }}>
                                      W_role_calculation
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                      }}>
                                      Tenure_calculation
                                    </Box>
                                    <Box
                                      component="th"
                                      sx={{
                                        background: 'rgba(236, 72, 153, 0.12)',
                                        border: '1px solid #e5e7eb',
                                        color: '#831843',
                                        fontWeight: 600,
                                        p: '6px 10px',
                                        textAlign: 'left',
                                      }}>
                                      Reason
                                    </Box>
                                  </Box>
                                </Box>
                                <Box component="tbody">
                                  {roleHistory.map(
                                    (role: any, index: number) => (
                                      <Box
                                        key={index}
                                        component="tr"
                                        sx={{
                                          '&:nth-of-type(even)': {
                                            background:
                                              'rgba(255, 255, 255, 0.6)',
                                          },
                                        }}>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            color: '#374151',
                                            p: '6px 10px',
                                          }}>
                                          {role.designation}
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            color: '#6b7280',
                                            p: '6px 10px',
                                          }}>
                                          {role.rank}
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            color: '#6b7280',
                                            p: '6px 10px',
                                          }}>
                                          {role.s_base}
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            color: '#6b7280',
                                            p: '6px 10px',
                                          }}>
                                          {role.w_role?.toFixed(2)}
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            p: '6px 10px',
                                          }}>
                                          <Box
                                            sx={{
                                              background:
                                                'rgba(251, 207, 232, 0.3)',
                                              borderRadius: '4px',
                                              color: '#831843',
                                              display: 'inline-block',
                                              fontFamily: 'monospace',
                                              fontSize: '0.8rem',
                                              px: 1,
                                              py: 0.5,
                                            }}>
                                            {role.w_role_calculation}
                                          </Box>
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            p: '6px 10px',
                                          }}>
                                          <Box
                                            sx={{
                                              background:
                                                'rgba(251, 207, 232, 0.3)',
                                              borderRadius: '4px',
                                              color: '#831843',
                                              display: 'inline-block',
                                              fontFamily: 'monospace',
                                              fontSize: '0.8rem',
                                              px: 1,
                                              py: 0.5,
                                            }}>
                                            {role.tenure_calculation || 'N/A'}
                                          </Box>
                                        </Box>
                                        <Box
                                          component="td"
                                          sx={{
                                            border: '1px solid #e5e7eb',
                                            color: '#6b7280',
                                            p: '6px 10px',
                                          }}>
                                          {role.reason}
                                        </Box>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })()}

                  {/* Skills Display (Primary & Secondary matched skills logic similar to the modal) */}
                  {(primarySkillAnalysis || secondarySkillAnalysis) && (
                    <Box className="skill-analysis-section">
                      {/* Similar UI elements adapted from modal */}
                    </Box>
                  )}
                  {Array.isArray(matchedSkills) &&
                    matchedSkills?.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography className="matched-skills-title">
                          ✓ Primary Matched Skills ({matchedSkills?.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {matchedSkills?.map((s, idx) => (
                            <Chip
                              key={idx}
                              label={
                                <>
                                  {s?.matchType === 'direct_primary'
                                    ? '✓✓'
                                    : s?.matchType === 'direct_secondary'
                                    ? '✓'
                                    : s?.matchType === 'ecosystem_primary'
                                    ? '≈'
                                    : s?.matchType === 'ecosystem_secondary'
                                    ? '~'
                                    : ''}{' '}
                                  {[
                                    'ecosystem_primary',
                                    'ecosystem_secondary',
                                  ].includes(s?.matchType) ? (
                                    <>
                                      <strong>{String(s?.skill)}</strong> →{' '}
                                      {String(s?.matchedWith)}{' '}
                                      {s?.score ? `(${String(s.score)})` : ''}
                                    </>
                                  ) : (
                                    <>
                                      {s?.matchedWith || s?.skill}{' '}
                                      {s?.score ? `(${String(s.score)})` : ''}
                                    </>
                                  )}
                                </>
                              }
                              sx={{
                                bgcolor:
                                  s?.score === 0.7
                                    ? 'rgba(99,102,241,0.1)'
                                    : '#dcfce7',
                                borderRadius: '6px',
                                color: s?.score === 0.7 ? '#6366f1' : '#15803d',
                                fontSize: '11px',
                                fontWeight: 500,
                              }}
                              size="small"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                  {/* Secondary Skills */}
                  {Array.isArray(secondarySkills) &&
                    secondarySkills?.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          sx={{
                            color: '#15803d',
                            fontSize: '11px',
                            fontWeight: 700,
                            mb: 1.5,
                            textTransform: 'uppercase',
                          }}>
                          ✓ Secondary Matched Skills ({secondarySkills?.length})
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}>
                          {secondarySkills?.map((s, i) => (
                            <Box
                              key={i}
                              sx={{
                                bgcolor:
                                  s?.score === 0.7
                                    ? 'rgba(99,102,241,0.1)'
                                    : '#f0fdf4',
                                border:
                                  s?.score === 0.7
                                    ? '1px solid #c7d2fe'
                                    : '1px solid #dcfce7',
                                borderRadius: '6px',
                                color: s?.score === 0.7 ? '#6366f1' : '#15803d',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1.5,
                                py: 0.5,
                              }}>
                              {s?.matchType === 'direct_primary'
                                ? '✓✓'
                                : s?.matchType === 'direct_secondary'
                                ? '✓'
                                : s?.matchType === 'ecosystem_primary'
                                ? '≈'
                                : s?.matchType === 'ecosystem_secondary'
                                ? '~'
                                : ''}{' '}
                              {[
                                'ecosystem_primary',
                                'ecosystem_secondary',
                              ].includes(s?.matchType) ? (
                                <span>
                                  <span
                                    style={{
                                      fontWeight: 'bold',
                                    }}>
                                    {String(s?.skill)}
                                  </span>{' '}
                                  → {String(s?.matchedWith)}{' '}
                                  {s?.score ? `(${String(s.score)})` : ''}
                                </span>
                              ) : (
                                <span>
                                  {s?.matchedWith || s?.skill}{' '}
                                  {s?.score ? `(${String(s.score)})` : ''}
                                </span>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CandidateMatchScoresScreen;
